"use client"

import { useState, useEffect } from "react"
import { APP_NAME, APP_NAME_ADMIN } from "@/lib/app-config"

const BRANDING_KEY = "branding"
const BRANDING_VERSION_KEY = "branding_v"
const MAX_IMAGE_BYTES = 512 * 1024 // 512 KB — skip base64 caching for oversized images

// Bump this whenever the cached-branding semantics change. Existing localStorage
// entries written under an older version are wiped on next page load, forcing
// a fresh /api/branding fetch. v2 introduces the default-logo cascade and
// background server refresh — older v1 entries (which may contain stale logos
// from removed domain_setup rows) must be invalidated. v3 adds signup_enabled
// (read by /trade/signup to gate the form on disabled domains) — pre-v3
// entries are missing the field and would default to undefined, so wipe them.
// v4 adds logo_url_dark + its cached logo_dark_data. This bump is load-bearing,
// not cosmetic: a v3 entry already holds the light logo inlined as base64, and
// the hook only re-fetches images when they are absent — so without the bump
// every returning visitor would keep painting the light logo in dark theme
// forever, and the fix would look like it had not shipped.
const BRANDING_SCHEMA_VERSION = 4

// Default brand logo bundled in /public. Used for dhan-fx.in and any domain
// that has no custom logo_url in domain_setup. Wide ~4:1 aspect.
export const DEFAULT_LOGO_URL = "/dhan-app-logo.svg"

export interface Branding {
  app_name: string | null
  /** Default logo. Rendered in light theme, and in BOTH themes when
   *  `logo_url_dark` is empty — which is the case for every tenant onboarded
   *  before theme-aware branding existed. */
  logo_url: string | null
  /** Dark-theme logo. `null` means "not configured": the resolution cascade
   *  below falls back to `logo_url`, so a single-logo tenant is unaffected. */
  logo_url_dark?: string | null
  loader_url: string | null
  // True iff this domain's admin (or the base-URL mutex holder) has
  // `allow_trader_self_signup=true` AND is non-demo + status=1. Source of
  // truth lives in the backend gate (auth.controller.js → traderSignup);
  // this field is a UX-layer mirror so the signup page can refuse to render
  // the form (and the login page can hide the "Sign up" link) without
  // round-tripping. Treat absence as `false` (fail-closed) — the backend
  // still enforces.
  signup_enabled?: boolean
}

/** Internal shape that includes cached data URLs */
interface CachedBranding extends Branding {
  logo_data?: string | null // base64 data URL for logo_url
  logo_dark_data?: string | null // base64 data URL for logo_url_dark
  loader_data?: string | null
}

/** Store branding received from login or getMe into localStorage */
export function storeBranding(branding: Branding | null | undefined) {
  if (branding) {
    localStorage.setItem(BRANDING_KEY, JSON.stringify(branding))
    localStorage.setItem(BRANDING_VERSION_KEY, String(BRANDING_SCHEMA_VERSION))
  }
}

/** Clear stored branding (only for admin role-switching, NOT logout) */
export function clearBranding() {
  memoryCache = null
  localStorage.removeItem(BRANDING_KEY)
  localStorage.removeItem(BRANDING_VERSION_KEY)
}

/** Read stored branding synchronously. Wipes the entry if the schema version
 *  is missing or stale — pre-v2 entries may contain a stale logo data URL
 *  from a removed domain_setup row that the (old) hook never re-fetched. */
function getStoredBranding(): CachedBranding | null {
  try {
    const storedVersion = Number(localStorage.getItem(BRANDING_VERSION_KEY) || 0)
    if (storedVersion !== BRANDING_SCHEMA_VERSION) {
      localStorage.removeItem(BRANDING_KEY)
      localStorage.setItem(BRANDING_VERSION_KEY, String(BRANDING_SCHEMA_VERSION))
      return null
    }
    const raw = localStorage.getItem(BRANDING_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return null
}

// In-memory branding cache — shared across all hook instances within the same page load.
let memoryCache: CachedBranding | null = null
let fetchPromise: Promise<void> | null = null

// Ceiling on a single branding-image fetch. These URLs are frequently on hosts
// we don't control, so an unbounded fetch can hang for the lifetime of the page.
// The consequence of giving up is only that the logo renders from its remote URL
// instead of a cached data URL — never a broken page.
const IMAGE_FETCH_TIMEOUT_MS = 8000

/** Fetch an image and return a base64 data URL. Returns null on failure or if too large. */
async function imageToDataUrl(url: string): Promise<string | null> {
  try {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null
    const timer = setTimeout(() => ctrl?.abort(), IMAGE_FETCH_TIMEOUT_MS)
    const res = await fetch(url, { signal: ctrl?.signal }).finally(() => clearTimeout(timer))
    if (!res.ok) return null
    const blob = await res.blob()
    if (blob.size > MAX_IMAGE_BYTES) return null
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

/**
 * Hook that returns dynamic branding.
 *
 * Images are fetched once, converted to base64 data URLs, and cached in
 * localStorage + memory. After the first domain visit, logos render from
 * local data — zero network requests, zero progressive loading.
 */
export function useBranding() {
  const [branding, setBranding] = useState<CachedBranding | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    // Helper: publish the branding, then cache its images in the background.
    //
    // `ready` flips as soon as the NAME is known and must NOT wait for the
    // images. `logo_url` frequently points at a third-party host (an icons8 CDN,
    // a broker's own asset server); when that host is slow, blocked or offline,
    // `imageToDataUrl` never settles. Awaiting it here used to pin every surface
    // that gates on `ready` in its skeleton state indefinitely — invisible while
    // those surfaces fell back to the base brand instead of a skeleton, and
    // immediately fatal once they stopped doing that. Text first, logo when it
    // arrives.
    const activate = async (b: CachedBranding, needsImageCache: boolean) => {
      if (cancelled) return
      setBranding(b)
      setReady(true)

      if (needsImageCache) {
        // Fetch images in parallel and convert to data URLs. The dark logo is
        // cached eagerly rather than on first theme flip: BrandLogo renders both
        // <img> tags and lets CSS pick, so a lazily-fetched dark logo would pop
        // in visibly the moment the user toggles.
        const [logoData, logoDarkData, loaderData] = await Promise.all([
          b.logo_url && !b.logo_data
            ? imageToDataUrl(b.logo_url)
            : Promise.resolve(b.logo_data ?? null),
          b.logo_url_dark && !b.logo_dark_data
            ? imageToDataUrl(b.logo_url_dark)
            : Promise.resolve(b.logo_dark_data ?? null),
          b.loader_url && !b.loader_data
            ? imageToDataUrl(b.loader_url)
            : Promise.resolve(b.loader_data ?? null),
        ])

        if (cancelled) return
        b.logo_data = logoData
        b.logo_dark_data = logoDarkData
        b.loader_data = loaderData
        memoryCache = b

        // Persist with embedded image data so next page load is instant
        try {
          localStorage.setItem(BRANDING_KEY, JSON.stringify(b))
          localStorage.setItem(BRANDING_VERSION_KEY, String(BRANDING_SCHEMA_VERSION))
        } catch { /* quota */ }

        setBranding({ ...b })
      }
    }

    // Background refresh — fetch /api/branding and, if the server's view differs
    // from the cached one, invalidate the cache. Without this, a logo removed
    // from `domain_setup` (or app_name renamed) lingers in browsers forever
    // because the original code only fetched when no cache existed.
    const refreshFromServer = async (currentCached: CachedBranding | null) => {
      try {
        const res = await fetch("/api/branding")
        if (cancelled) return
        const data = await res.json().catch(() => null)
        if (!data?.success) return
        const fresh: Branding = {
          app_name: data.data?.app_name || null,
          logo_url: data.data?.logo_url || null,
          logo_url_dark: data.data?.logo_url_dark || null,
          loader_url: data.data?.loader_url || null,
          signup_enabled: !!data.data?.signup_enabled,
        }

        // logo_url_dark MUST participate in this comparison. A tenant that
        // later adds a dark logo changes nothing else about their branding, so
        // omitting it here would make the new logo invisible to every browser
        // holding a cache entry until the next schema-version bump.
        const same =
          currentCached &&
          fresh.app_name === (currentCached.app_name ?? null) &&
          fresh.logo_url === (currentCached.logo_url ?? null) &&
          fresh.logo_url_dark === (currentCached.logo_url_dark ?? null) &&
          fresh.loader_url === (currentCached.loader_url ?? null) &&
          fresh.signup_enabled === !!currentCached.signup_enabled
        if (same) return

        // Server data changed (logo removed / app renamed / loader swapped /
        // signup flag toggled). Replace the cache. If all visual fields are
        // empty AND signup is disabled, drop the entry entirely so the
        // default-logo cascade kicks in — but keep an entry whenever any
        // field carries information (e.g. unbranded base URL where signup
        // is enabled still needs the flag persisted).
        if (
          !fresh.app_name &&
          !fresh.logo_url &&
          !fresh.logo_url_dark &&
          !fresh.loader_url &&
          !fresh.signup_enabled
        ) {
          memoryCache = null
          try { localStorage.removeItem(BRANDING_KEY) } catch { /* ignore */ }
          if (!cancelled) {
            setBranding(null)
            setReady(true)
          }
          return
        }
        const next: CachedBranding = { ...fresh }
        memoryCache = next
        storeBranding(next)
        await activate(next, true)
      } catch { /* network error — keep current cache */ }
    }

    // 1. In-memory cache (instant — same page session). Still refresh in the
    //    background so server-side branding changes propagate without a hard
    //    cache wipe.
    if (memoryCache) {
      setBranding(memoryCache)
      setReady(true)
      refreshFromServer(memoryCache)
      return () => { cancelled = true }
    }

    // 2. localStorage (persists across navigations) — paint instantly, then
    //    refresh in the background to catch server-side changes.
    const stored = getStoredBranding()
    if (stored) {
      memoryCache = stored
      // If images aren't cached yet (e.g. old branding entry), cache them now
      const needsImageCache =
        (!!stored.logo_url && !stored.logo_data) ||
        (!!stored.logo_url_dark && !stored.logo_dark_data) ||
        (!!stored.loader_url && !stored.loader_data)
      activate(stored, needsImageCache)
      refreshFromServer(stored)
      return () => { cancelled = true }
    }

    // 3. Fetch from API (no cache yet)
    if (!fetchPromise) {
      fetchPromise = fetch("/api/branding")
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.data) {
            const b: CachedBranding = {
              app_name: data.data.app_name || null,
              logo_url: data.data.logo_url || null,
              logo_url_dark: data.data.logo_url_dark || null,
              loader_url: data.data.loader_url || null,
              signup_enabled: !!data.data.signup_enabled,
            }
            // Persist the entry whenever ANY field carries information —
            // including signup_enabled=true on an otherwise unbranded base
            // URL (signup page reads this on next mount to decide whether
            // to render the form).
            if (b.app_name || b.logo_url || b.logo_url_dark || b.loader_url || b.signup_enabled) {
              memoryCache = b
              storeBranding(b)
            }
          }
        })
        .catch(() => { /* ignore — env defaults used */ })
        .finally(() => { fetchPromise = null })
    }

    fetchPromise.then(() => {
      if (cancelled) return
      if (memoryCache) {
        activate(memoryCache, true)
      } else {
        setReady(true)
      }
    })

    return () => { cancelled = true }
  }, [])

  const appName = branding?.app_name || APP_NAME
  const appNameAdmin = branding?.app_name ? `${branding.app_name} Admin` : APP_NAME_ADMIN
  // Prefer cached data URL (zero network) over remote URL. Cascade to the
  // bundled DEFAULT_LOGO_URL so dhan-fx.in and any unbranded tenant always
  // render the DHAN-FX wordmark instead of a per-page inline diamond fallback.
  const customLogoUrl = branding?.logo_data || branding?.logo_url || null
  const logoUrl = customLogoUrl || DEFAULT_LOGO_URL
  const isDefaultLogo = !customLogoUrl

  // Dark-theme logo. The cascade dark → light is what keeps every existing
  // single-logo tenant byte-identical: with no `logo_url_dark` configured,
  // `logoUrlDark === logoUrl`, BrandLogo collapses to a single <img>, and the
  // rendered markup is exactly what it was before theme-aware branding.
  //
  // The bundled DEFAULT_LOGO_URL needs no dark variant — it is a wordmark in
  // fixed brand colours (#0D5BFF / #FF2E2E / #12B84F) that reads on both
  // backgrounds, so an unbranded tenant also collapses to the single-<img>
  // path.
  const customLogoDarkUrl = branding?.logo_dark_data || branding?.logo_url_dark || null
  const logoUrlDark = customLogoDarkUrl || logoUrl
  /** True only when this tenant configured a distinct dark-theme logo. */
  const hasThemedLogo = logoUrlDark !== logoUrl
  const loaderUrl = branding?.loader_data || branding?.loader_url || null
  // signupEnabled: undefined until `ready` flips true (callers should gate
  // their UI on `ready`). Fail-closed once known — only `true` unlocks the
  // form; missing/false locks it.
  const signupEnabled = !!branding?.signup_enabled

  return {
    appName,
    appNameAdmin,
    logoUrl,
    logoUrlDark,
    hasThemedLogo,
    loaderUrl,
    isDefaultLogo,
    ready,
    signupEnabled,
  }
}
